import { inject, Injectable } from '@angular/core';
import { ScenarioSankeyStatesEnum } from '@app/scenarios/enums/ScenarioSankeyStatesEnum';
import { ScenarioResultFacade } from '@app/scenarios/facades/scenario-result.facade';
import { CoreUrlProvider } from '@core/config/core-url.provider';
import { AuthFacade } from '@core/facades/auth.facade';
import { environment } from '@environments/environment';
import { NotifyService } from '@hal/common';
import { EventSourceMessage, fetchEventSource } from '@microsoft/fetch-event-source';

interface EventData {
  eventType: EventTypesEnum;
}

interface ScenarioEventData extends EventData {
  scenarioUuid: string;
}

interface Event {
  message: string;
  title: string;
  type: 'none' | 'success' | 'warning' | 'error' | 'info';
  data: EventData;
}

enum EventTypesEnum {
  SankeyStart = 'SankeyStart',
  SankeyResult = 'SankeyResult',
  SankeyStale = 'SankeyStale',
  SankeyFail = 'SankeyFail',
  RetentionResult = 'RetentionResult',
  RetentionFail = 'RetentionFail',
}

@Injectable({
  providedIn: 'root',
})
export class SseService {
  private readonly _authFacade: AuthFacade = inject(AuthFacade);
  private readonly _notifyService: NotifyService = inject(NotifyService);
  private readonly _scenarioResultFacade: ScenarioResultFacade = inject(ScenarioResultFacade);

  private readonly hiddenNotifications = new Set([EventTypesEnum.SankeyStart, EventTypesEnum.SankeyStale]);

  public async connect(): Promise<void> {
    const accessToken = this._authFacade.accessToken;
    await fetchEventSource(`${environment.apiUrl}${CoreUrlProvider.URL.SSE.BASE}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/octet-stream',
      },
      onmessage: (ev: EventSourceMessage) => {
        const event: Event = JSON.parse(ev.data);

        if (!this.hiddenNotifications.has(event.data.eventType)) {
          this._notifyService.showNotification({ type: event.type, messageRaw: event.message });
        }
        this.handleData(event.data);
      },
    });
  }

  private handleData(data: EventData) {
    switch (data.eventType) {
      case EventTypesEnum.SankeyStart: {
        this._scenarioResultFacade.setResultState(
          (data as ScenarioEventData).scenarioUuid,
          ScenarioSankeyStatesEnum.IN_PROGRESS
        );
        break;
      }
      case EventTypesEnum.SankeyResult: {
        this._scenarioResultFacade.setResultState(
          (data as ScenarioEventData).scenarioUuid,
          ScenarioSankeyStatesEnum.COMPLETED
        );
        break;
      }
      case EventTypesEnum.SankeyStale:
      case EventTypesEnum.SankeyFail: {
        this._scenarioResultFacade.setResultState(
          (data as ScenarioEventData).scenarioUuid,
          ScenarioSankeyStatesEnum.FAILED
        );
        break;
      }
    }
  }
}
